import Icon from '@/components/common/Icon';
import styles from '@/styles/modal.module.scss';
import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Modal, notification, Spin, Typography } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const { Text } = Typography;

const AddMonitorModal = ({
  open,
  onCancel,
  mode = 'manual', // 'manual' or 'auto' (for new users)
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [form] = Form.useForm();
  const router = useRouter();

  // Reset internal state every time the modal opens. The component stays
  // mounted across opens/closes (Dashboard always renders it), so without
  // this, `analyzing` from a prior successful submission stays stuck true
  // and the next open renders the "Analyzing..." spinner forever.
  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setAnalyzing(false);
      form.resetFields();
    }
  }, [open, form]);

  // Domain validation regex
  const domainRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  const validateDomain = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a domain name'));
    }

    // Remove protocol if user includes it
    const cleanDomain = value.replace(/^https?:\/\//, '').replace(/\/$/, '');

    if (!domainRegex.test(cleanDomain)) {
      return Promise.reject(new Error('Please enter a valid domain name (e.g., example.com)'));
    }

    if (cleanDomain.length > 253) {
      return Promise.reject(new Error('Domain name is too long'));
    }

    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();

      // Clean the domain input
      const cleanDomain = values.domain
        .replace(/^https?:\/\//, '')
        .replace(/\/$/, '')
        .toLowerCase();

      // Show analyzing state
      setAnalyzing(true);

      // Call API to create monitor
      const response = await fetch('/api/account/monitors/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: cleanDomain,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create monitor');
      }

      const result = await response.json();

      notification.success({
        message: 'Monitor Created Successfully',
        description: `Started monitoring ${cleanDomain}. Redirecting to monitor details...`,
        duration: 3,
      });

      // Set flag to invalidate dashboard cache on next visit
      sessionStorage.setItem('dashboard_force_refresh', Date.now().toString());
      console.log('[AddMonitorModal] Set cache invalidation flag after monitor creation');

      // Close modal and redirect after short delay
      setTimeout(() => {
        onCancel();
        router.push(`/account/monitors/${cleanDomain}`);
      }, 1500);
    } catch (error) {
      console.error('Error creating monitor:', error);

      notification.error({
        message: 'Error Creating Monitor',
        description: error.message || 'Failed to create monitor. Please try again.',
        duration: 5,
      });

      setAnalyzing(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!submitting && !analyzing) {
      form.resetFields();
      onCancel();
    }
  };

  return (
    <Modal
      title="Add Monitor"
      open={open}
      onCancel={handleCancel}
      className={styles.customModal}
      maskClosable={false}
      destroyOnClose
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={submitting || analyzing}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={analyzing}
          icon={analyzing ? <LoadingOutlined /> : <Icon name="Plus" size={16} />}
        >
          {analyzing ? 'Analyzing Domain...' : 'Create Monitor'}
        </Button>,
      ]}
    >
      {analyzing ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Text strong style={{ fontSize: '16px' }}>
              Analyzing your domain...
            </Text>
            <br />
            <Text type="secondary">
              We're checking DNS records, DMARC policy, BIMI configuration, and more.
            </Text>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '24px' }}>
            <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
              Add a Monitor to analyze how your sending domain and branded logo displays across
              BIMI-supported Mailboxes such as Gmail, Apple Mail, and Yahoo!.
            </Text>
          </div>

          {mode === 'auto' && (
            <Alert
              type="info"
              message="Welcome to Brandymail!"
              description="Let's get started by adding your first domain to monitor. This will help you track how your branded emails appear in major email clients."
              style={{ marginBottom: '24px' }}
            />
          )}

          <Form form={form} layout="vertical">
            <Form.Item
              label="Enter Sending Domain"
              name="domain"
              rules={[
                { required: true, message: 'Domain is required' },
                { validator: validateDomain },
              ]}
              validateTrigger={['onBlur', 'onSubmit']}
              extra="Enter your domain without 'http://' or 'www' (e.g., example.com)"
            >
              <Input
                placeholder="example.com"
                size="large"
                prefix={<Icon name="World" size={16} color="#666" />}
                onPressEnter={handleSubmit}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default AddMonitorModal;
